import { useEffect, useState } from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';

import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { apiPost } from '@/utils/api';
import {
  clearStash, impersonationFrom, readStash, secondsRemaining,
} from '@/utils/impersonation';

/**
 * "Impersonating Jane Doe — your actions are logged".
 *
 * 16_SCENARIOS §15.1 asked for a banner across the screen in those words. This
 * says one thing more than the document does — READ-ONLY — because the feature
 * built beneath it refuses every write, and a super admin who does not know
 * that will read a 403 as broken access and ask for more of it.
 *
 * 🔴 IT SITS ABOVE THE SCROLL AREA, NOT INSIDE IT. VerifyPasswordBanner is a
 * child of <main>, which is the element that scrolls, so it leaves the screen
 * as soon as anyone reads a long page. That is right for a reminder about a
 * password and wrong for this: the one moment the banner matters is the moment
 * somebody has forgotten whose account they are looking at, and by then they
 * have scrolled. Placed as the first child of the content column — a flex
 * column that does not scroll — it cannot leave.
 *
 * The countdown is not decoration. The grant is fifteen minutes with no
 * refresh, so the alternative to showing the time left is a session that dies
 * mid-sentence and dumps them at the login page.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const accessToken = useAuthStore((s) => s.accessToken);
  const setTokens = useAuthStore((s) => s.setTokens);
  const claims = impersonationFrom(accessToken);

  const [left, setLeft] = useState(() => (claims ? secondsRemaining(claims) : 0));
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    if (!claims) return;
    setLeft(secondsRemaining(claims));
    const t = window.setInterval(() => setLeft(secondsRemaining(claims)), 1000);
    return () => window.clearInterval(t);
    // `claims` is derived from the token, so this re-arms when the token does.
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Hand the super admin back their own session.
   *
   * 🔴 A FULL RELOAD, NOT A STATE SWAP. Every React Query cache on the page
   * holds the TARGET's rows. Restoring the token in place would leave those
   * rows rendered under the super admin's own identity — the one confusion
   * this whole feature exists to prevent — until each query happened to
   * refetch. The reload throws all of it away at once.
   */
  const stop = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      // Sent with the impersonation token: that is how the route knows both
      // parties without being told either, and it is what closes the audit
      // pair. A failure here must NOT strand them in the session, so the
      // restore happens either way.
      await apiPost('/admin/impersonate/stop', {});
    } catch {
      /* the grant expires on its own; being unable to log the end is not a
         reason to keep somebody inside another person's account */
    }
    const mine = readStash();
    clearStash();
    if (mine) {
      setTokens(mine.accessToken, mine.refreshToken);
      window.location.href = '/dashboard';
    } else {
      // No stash — a different tab, or storage that refused to keep it. Their
      // own session cannot be restored, so end this one rather than leave an
      // impersonated token in a browser with no banner to explain it.
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
  };

  // The grant ran out. Returning them to themselves is better than waiting for
  // the next request to 401 and clear the session out from under them.
  useEffect(() => {
    if (claims && left === 0 && !stopping) void stop();
  }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!claims) return null;

  const name = user?.full_name || user?.email || 'this user';
  const mins = Math.floor(left / 60);
  const secs = String(left % 60).padStart(2, '0');

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      className="shrink-0 border-b border-amber-500 bg-amber-400 px-4 py-2 text-sm text-amber-950"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1">
        <EyeIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="flex-1 min-w-0">
          <strong className="font-semibold">Impersonating {name}</strong>
          {' — read-only, and your actions are logged.'}
          {claims.reason ? (
            <span className="ml-1 opacity-80">Reason: {claims.reason}</span>
          ) : null}
        </span>
        <span className="tabular-nums font-medium" data-testid="impersonation-remaining">
          {mins}:{secs} left
        </span>
        <button
          type="button"
          onClick={stop}
          disabled={stopping}
          data-testid="stop-impersonating"
          className="rounded-lg bg-amber-950 px-3 py-1 font-semibold text-amber-50 hover:bg-amber-900 disabled:opacity-60"
        >
          {stopping ? 'Stopping…' : 'Stop impersonating'}
        </button>
      </div>
    </div>
  );
}
